import type { ActionListQuery, PlatformActionPlatformDto } from '../models/platformAction.wire'
import { adaptPage } from '../service/adaptActionRequest'
import { classifyError, isCanceled } from '../service/classifyError'

import type { ListActionsApi, ListActionsQuery, ListActionsStore } from './platformActionFlow.types'

export interface LoadActionsFlowDeps {
  api: ListActionsApi
  store: ListActionsStore
}

/**
 * Identity of one list request. A filter, page or workspace change makes an in-flight response
 * stale, and the store only lets the current key write (same guard as `LoadAnomaliesFlow`).
 */
export function listKey(workspaceId: number, query: ListActionsQuery): string {
  return [workspaceId, query.status, query.platform, query.page, query.pageSize].join('|')
}

/** Empty filter values mean "all" and must not be sent — `?status=` is not the same as no filter. */
function buildQuery(query: ListActionsQuery): ActionListQuery {
  const params: ActionListQuery = { page: query.page, page_size: query.pageSize }
  if (query.status) params.status = query.status
  if (query.platform) params.platform = query.platform as PlatformActionPlatformDto
  return params
}

export class LoadActionsFlow {
  constructor(private readonly deps: LoadActionsFlowDeps) {}

  async execute(workspaceId: number, query: ListActionsQuery, signal?: AbortSignal): Promise<void> {
    const key = listKey(workspaceId, query)
    this.deps.store.setLoading(key)

    try {
      const body = await this.deps.api.list(workspaceId, buildQuery(query), signal)
      if (signal?.aborted === true) return
      this.deps.store.setPage(key, adaptPage(body))
    } catch (cause) {
      const error = classifyError(cause)
      // A canceled request is a navigation artifact, never an error state the user should see.
      if (isCanceled(error)) return
      this.deps.store.setError(key, error)
    }
  }
}
